import React, { useEffect, useRef } from 'react';
import { Flex, RemoteImage, useScreenSize } from '@banhala/ui-kit';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import type { useRetailAds } from '../../queries/useRetailAds';
import { usePostRetailAdDisplayEvent } from '../../queries/usePostRetailAdDisplayEvent';

interface RetailAdsProps {
  retailAds: ReturnType<typeof useRetailAds>['data'];
}

export const RetailAds = ({ retailAds }: RetailAdsProps) => {
  const { width } = useScreenSize();
  const { openDeeplink } = useOpenDeeplink();
  const previousImageURL = useRef<string | null>(null);
  const { mutate: sendDisplayEvent } = usePostRetailAdDisplayEvent();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const imageUrl = retailAds?.ad_info?.creative_media_link;
  const targetUrl = retailAds?.ad_info?.creative_link;

  const ratio = retailAds?.ad_info?.creative_media_ratio ?? 7.8125;
  const height = width / ratio;

  useEffect(() => {
    if (!imageUrl) return;
    if (imageUrl === previousImageURL.current) return;

    previousImageURL.current = imageUrl;
    logAnalyticsEvent({
      name: 'DISPLAY_ADS',
      channel: ['firebase', 'ablylog'],
      analytics: {
        AD_ID: retailAds?.ad_info?.ad_executable_id,
        AD_PLACEMENT_ID: retailAds?.ad_info?.ad_placement_id,
        ADVERTISING_SUPPLIER: retailAds?.ad_info?.ad_supplier,
        AD_NAME: retailAds?.ad_info?.ad_executable_name,
        CREATIVE_TYPE: retailAds?.ad_info?.creative_type,
        LINK: retailAds?.ad_info?.creative_link,
      },
    });

    // iOS: SearchAblyAdCell → retailAdClient.sendDisplayEvent
    const executableId = retailAds?.ad_info?.ad_executable_id;
    if (executableId != null) {
      sendDisplayEvent(executableId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  const handleClick = () => {
    logAnalyticsEvent({
      name: 'CLICK_ADS',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        AD_ID: retailAds?.ad_info?.ad_executable_id,
        AD_PLACEMENT_ID: retailAds?.ad_info?.ad_placement_id,
        ADVERTISING_SUPPLIER: retailAds?.ad_info?.ad_supplier,
        AD_NAME: retailAds?.ad_info?.ad_executable_name,
        CREATIVE_TYPE: retailAds?.ad_info?.creative_type,
        LINK: retailAds?.ad_info?.creative_link,
      },
    });

    if (targetUrl?.startsWith('ably://')) {
      openDeeplink(targetUrl);
    } else if (targetUrl) {
      const deeplink = `ably://webview?url=${targetUrl}`;
      openDeeplink(deeplink);
    }
  };

  if (!imageUrl) return null;

  return (
    <Flex.Row onPress={handleClick}>
      <RemoteImage uri={imageUrl} width={width} height={height} />
    </Flex.Row>
  );
};
